import Fuse, { FuseOptions, FuseResultWithScore } from 'fuse.js';
import { useEffect, useMemo, useState } from 'react';
import { StationeryTemplate } from '../../DesignsGallery.types';
import { useValuePropsPayload } from '../../useValuePropsPayload';
import { GalleryTemplate, GalleryValueProp } from '../GalleryItem/GalleryItem';
import { EMBEDDINGS_STORE, SIMILARITY_THRESHOLD } from './Search.constants';
import { useSearchContext } from './SearchContext';
import { colorSimilarity, cosineSimilarity } from './utils';

export type Embedding = ReadonlyArray<number>;

type PrecomputedEmbeddings = Readonly<{
  embeddingSizeBytes: number;
  keywords: string[];
  themes: Record<string, Readonly<[ReadonlyArray<number>, Readonly<[number, number, number]> | [], ReadonlyArray<number>]>>;
}>;

function usePrecomputedEmbeddings() {
  const [themesAndEmbeddings, setThemesAndEmbeddings] = useState<{
    themes: Map<string, { concepts: ReadonlyArray<Embedding>; colors: Readonly<[Embedding, Embedding, Embedding]> | []; customs: ReadonlyArray<Embedding> }> | undefined;
    embeddingMap: Map<string, Embedding>;
  }>({
    themes: undefined,
    embeddingMap: new Map<string, Embedding>(),
  });

  useEffect(() => {
    void (async () => {
      const [bundle, bigBuffer] = (await Promise.all([
        fetch(`${EMBEDDINGS_STORE}/themes.json`).then(r => r.json()),
        fetch(`${EMBEDDINGS_STORE}/themes.bin`).then(r => r.arrayBuffer()),
      ])) as [PrecomputedEmbeddings, ArrayBuffer];

      const embeddings = bundle.keywords.map((_, i) => {
        const keywordBuffer = bigBuffer.slice(i * bundle.embeddingSizeBytes, (i + 1) * bundle.embeddingSizeBytes);
        return Array.from(new Float32Array(keywordBuffer)) as Embedding;
      });

      const themes = new Map(
        // Object.entries is >95% the compat table is being updated soon.
        // eslint-disable-next-line compat/compat
        Object.entries(bundle.themes).map(([id, [conceptIndexes, colorIndexes, customIndexes]]) => {
          return [
            id,
            {
              concepts: conceptIndexes.map(i => embeddings[i]),
              colors: colorIndexes.length === 0 ? ([] as []) : (colorIndexes.map(i => embeddings[i]) as Readonly<[Embedding, Embedding, Embedding]> | []),
              customs: customIndexes.map(i => embeddings[i]),
            },
          ];
        }),
      );
      setThemesAndEmbeddings({
        themes,
        embeddingMap: new Map(bundle.keywords.map((keyword, i) => [keyword, embeddings[i]])),
      });
    })();
  }, []);

  return themesAndEmbeddings;
}

function getThemeIdOrEquivalent(item: GalleryTemplate | GalleryValueProp) {
  if (item.type === 'prop') return `valueprop_${item.telemetryId ?? ''}`;
  return item.themeId;
}

export function useSearchFilteredGalleryItems(templates: ReadonlyArray<GalleryTemplate>, fuse: Fuse<StationeryTemplate, FuseOptions<StationeryTemplate>>) {
  const { themes, embeddingMap } = usePrecomputedEmbeddings();
  const { activeTerm: term, activeTermEmbedding: termEmbedding } = useSearchContext();
  const isActive = !!termEmbedding;
  const valuePropsData = useValuePropsPayload();

  const filteredItems = useMemo(() => {
    if (!themes) return templates; // Means still loading
    if (!isActive) return templates;

    const valueProps: ReadonlyArray<GalleryValueProp> = (valuePropsData?.props ?? []).map(prop => ({
      ...prop,
      type: 'prop',
    }));
    const items = [...templates, ...valueProps];

    const similarityMap = new Map<string, number>();

    // Adding a space in front of term to deter term "red" from overmatching "mirrored", "multicolored", etc.
    const lexicalDistanceResults = fuse.search(` ${term}`) as FuseResultWithScore<StationeryTemplate>[];
    const lexicalSimilarityMap = new Map(lexicalDistanceResults.map(r => [r.item.themeId, 1 - (r.score ?? 1)]));

    for (const item of items) {
      const themeId = getThemeIdOrEquivalent(item);
      const theme = themes.get(themeId);

      const similarityLexical = lexicalSimilarityMap.get(themeId) ?? 0;
      const similarityConcept = Math.max(0, ...(theme?.concepts ?? []).map(embedding => cosineSimilarity(termEmbedding, embedding)));
      const similarityCustom = Math.max(0, ...(theme?.customs ?? []).map(embedding => cosineSimilarity(termEmbedding, embedding)));

      // See `colorSimilarity` for why white and beige are passed-in.
      const white = embeddingMap.get('white')!; // Well-known values in emMap
      const beige = embeddingMap.get('beige')!; // Well-known values in emMap
      const similarityColor = colorSimilarity(termEmbedding, theme?.colors ?? [], white, beige);

      const similarity = Math.max(
        similarityLexical * 1.01, // A perfect lexical match ranks above all else. +0.1 to tie break.
        similarityConcept,
        similarityColor,
        similarityCustom,
      );

      similarityMap.set(themeId, similarity);
    }

    const filteredItems = items.filter(template => (similarityMap.get(getThemeIdOrEquivalent(template)) ?? 0) >= SIMILARITY_THRESHOLD);
    filteredItems.sort((a, b) => (similarityMap.get(getThemeIdOrEquivalent(b)) ?? 0) - (similarityMap.get(getThemeIdOrEquivalent(a)) ?? 0));
    return filteredItems;
  }, [templates, valuePropsData, fuse, themes, embeddingMap, term, termEmbedding, isActive]);

  return {
    isActive,
    filteredItems,
  };
}
